import { c as _c } from "react/compiler-runtime";
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/actions/api";
import { useToast } from "@/hooks/use-toast";
import { RealEstateType } from "@/types/real-estate";
import { Query } from "@/types/queryTpes";
import { QueryKeys } from "@/constants/queryKeys";
import uuidSlugUtil from "@/utils/uuidSlugUtil";
import useRealEstateGroups from "@/hooks/QueryHooks/useRealEstateGroups";
import { useIsMobile } from "@/hooks/use-mobile";
import { queryKeysFavoritesGroups } from "../query-options";
export const useToggleRealEstateInGroup = realEstateId => {
  const $ = _c(15);
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const {
    data: realEstateFavoriteGroups
  } = useRealEstateGroups(realEstateId);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== realEstateFavoriteGroups?.data) {
    t0 = async variables => {
      const {
        realEstateId: realEstateId_1
      } = variables;
      const queryKey = uuidSlugUtil(undefined, realEstateId_1);
      const newIsFavorited = variables.isFavorite || realEstateFavoriteGroups?.data.length > 1;
      const shouldIncreaseCount = variables.isFavorite && realEstateFavoriteGroups?.data.length === 0;
      const shouldDecreseCount = !variables.isFavorite && realEstateFavoriteGroups?.data.length === 1;
      const previousListQueries = queryClient.getQueriesData({
        queryKey: [QueryKeys.REAL_ESTATE]
      });
      const previousDetailData = queryClient.getQueryData([QueryKeys.REAL_ESTATE_DETAIL, queryKey]);
      queryClient.setQueriesData({
        queryKey: [QueryKeys.REAL_ESTATE]
      }, oldData => {
        if (!oldData) {
          return oldData;
        }
        const updatedPages = oldData.pages.map(page => ({
          ...page,
          data: page.data.map(item => {
            if (item.realEstate.id === realEstateId_1) {
              const prevCount = item.analytics?.favoritesCount ?? 0;
              const delta = newIsFavorited ? 1 : -1;
              const nextCount = Math.max(0, prevCount + delta);
              return {
                ...item,
                isFavorite: newIsFavorited,
                analytics: {
                  ...item.analytics,
                  favoritesCount: nextCount
                }
              };
            }
            return item;
          })
        }));
        return {
          ...oldData,
          pages: updatedPages
        };
      });
      queryClient.setQueryData([QueryKeys.REAL_ESTATE_DETAIL, queryKey], old => old ? {
        ...old,
        data: {
          ...old.data,
          isFavorite: newIsFavorited,
          analytics: {
            ...old.data.analytics,
            favoritesCount: Math.max(0, (old.data.analytics?.favoritesCount ?? 0) + (shouldIncreaseCount ? 1 : shouldDecreseCount ? -1 : 0))
          }
        }
      } : old);
      return {
        previousListQueries,
        previousDetailData
      };
    };
    $[0] = queryClient;
    $[1] = realEstateFavoriteGroups?.data;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== queryClient || $[4] !== toast) {
    t1 = (err, variables_0, context) => {
      console.log(err);
      const queryKey_0 = uuidSlugUtil(undefined, variables_0.realEstateId);
      if (context?.previousListQueries) {
        for (const [key, data] of context.previousListQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousDetailData) {
        queryClient.setQueryData([QueryKeys.REAL_ESTATE_DETAIL, queryKey_0], context.previousDetailData);
      }
      toast({
        variant: "destructive",
        title: "Erro ao atualizar im\xF3vel no grupo",
        description: "Ocorreu um erro ao atualizar o im\xF3vel no grupo."
      });
    };
    $[3] = queryClient;
    $[4] = toast;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== isMobile || $[7] !== queryClient || $[8] !== realEstateId || $[9] !== toast) {
    t2 = (response, variables_1) => {
      if (!isMobile) {
        toast({
          variant: "success",
          description: "Favorito atualizado"
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeysFavoritesGroups.all
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ESTATE_FAV_GROUPS, realEstateId]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.FAV_GROUP_ESTATES, variables_1.groupId]
      });
    };
    $[6] = isMobile;
    $[7] = queryClient;
    $[8] = realEstateId;
    $[9] = toast;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t0 || $[12] !== t1 || $[13] !== t2) {
    t3 = {
      mutationFn: _temp,
      onMutate: t0,
      onError: t1,
      onSuccess: t2
    };
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return useMutation(t3);
};
function _temp(t0) {
  const {
    groupId,
    realEstateId: realEstateId_0
  } = t0;
  return api.favoriteGroups.toggleRealEstateInGroup({
    groupId,
    realEstateId: realEstateId_0
  });
}